import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
	CalendarDaysIcon,
	CheckCircleIcon,
	CurrencyDollarIcon,
	ExclamationTriangleIcon,
	DocumentTextIcon,
	// WrenchScrewdriverIcon, // TODO: Re-enable once skills & tasks editing is enabled
	ClipboardDocumentListIcon,
	HandRaisedIcon,
	ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid';
import AppLayout from '../../components/layout/AppLayout';
import { Alert, AppLoader } from '../../components/ui';
import useAuthStore from '../../stores/authStore';
import useStaffProfileStore from '../../stores/staffProfileStore';
import useNotificationsStore from '../../stores/notificationsStore';
import { formatInfoRequestDate } from '../../utils/infoRequest';
import type { StaffDashboardAppointment } from '../../types/staff';

// Navigation state type
interface LocationState {
	passwordJustChanged?: boolean;
}

// Helper to format currency
const formatCurrency = (amount: number): string => {
	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
	}).format(amount);
};

const DIVIDER_COLORS = [
	'bg-emerald-500',
	'bg-amber-400',
	'bg-blue-500',
	'bg-purple-500',
	'bg-pink-500',
	'bg-teal-500',
];

const StaffDashboard = () => {
	const { user } = useAuthStore();
	const navigate = useNavigate();
	const location = useLocation();
	const locationState = location.state as LocationState | null;
	const {
		profile,
		dashboard,
		isLoadingProfile,
		isLoadingDashboard,
		profileError,
		dashboardError,
		fetchProfile,
		fetchDashboard,
	} = useStaffProfileStore();
	const [dismissedAlerts, setDismissedAlerts] = useState<string[]>([]);

	// Check if password was just changed
	const passwordJustChanged = locationState?.passwordJustChanged ?? false;

	// Fetch profile if not already loaded
	useEffect(() => {
		if (!profile && !isLoadingProfile && !profileError) {
			fetchProfile();
		}
	}, [profile, isLoadingProfile, profileError, fetchProfile]);

	// Fetch dashboard data on mount
	useEffect(() => {
		fetchDashboard();
	}, [fetchDashboard]);

	const dismissAlert = (id: string) => {
		setDismissedAlerts(prev => [...prev, id]);
	};

	const showAlert = (id: string) => !dismissedAlerts.includes(id);

	// Personal-info request (ambient nudge; the modal is the primary prompt)
	const infoRequestState = useNotificationsStore(state => state.infoRequestState);
	const infoRequestExpiresOn = useNotificationsStore(state =>
		formatInfoRequestDate(state.infoRequest?.expiresOn ?? null)
	);
	const openInfoRequestModal = useNotificationsStore(state => state.openInfoRequestModal);

	// Retry fetching all data
	const handleRetry = () => {
		fetchProfile();
		fetchDashboard();
	};

	// Show fullscreen loader while loading initial data
	const isInitialLoading = (isLoadingProfile && !profile) || (isLoadingDashboard && !dashboard);
	if (isInitialLoading) {
		return <AppLoader message="Loading dashboard" variant="fullscreen" />;
	}

	// Show error page if profile failed to load
	if (profileError && !profile) {
		return (
			<AppLayout>
				<Alert
					type="warning"
					icon={<ExclamationTriangleIcon className="w-8 h-8" />}
					title="Unable to Load Profile"
					message={profileError}
					actions={[{ label: 'Retry', onClick: handleRetry, primary: true }]}
					onDismiss={() => {}}
				/>
			</AppLayout>
		);
	}

	// Build KPI stats
	const dashboardStats = [
		{
			id: 'weekly-appointments',
			value: dashboard ? dashboard.appointmentsThisWeek.toString() : '0',
			label: 'Bookings This Week',
			icon: CalendarDaysIcon,
			colorClass: 'bg-gradient-to-br from-blue-100 to-blue-200',
			link: '/staff/bookings',
		},
		{
			id: 'monthly-completed',
			value: dashboard ? dashboard.completedThisMonth.toString() : '0',
			label: 'Completed This Month',
			icon: CheckCircleIcon,
			colorClass: 'bg-gradient-to-br from-green-100 to-green-200',
			link: '/staff/tcr',
		},
		{
			id: 'monthly-earnings',
			value: dashboard ? formatCurrency(dashboard.monthlyEarnings) : '$0',
			label: 'Monthly Earnings',
			icon: CurrencyDollarIcon,
			colorClass: 'bg-gradient-to-br from-purple-100 to-purple-200',
		},
	];

	// Today's bookings
	const bookings: StaffDashboardAppointment[] = dashboard?.todayAppointments || [];

	// Combined error
	const error = profileError || dashboardError;

	return (
		<AppLayout>
			{/* Welcome Banner */}
			<div className="bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white p-6 md:p-10 rounded-2xl mb-6 shadow-lg flex justify-between items-center flex-wrap gap-5">
				<div>
					<h2 className="text-xl md:text-2xl font-bold mb-2 flex items-center gap-3">
						Welcome{user?.name ? `, ${user.name}` : ''}!
						<HandRaisedIcon className="w-7 h-7 inline-block" />
					</h2>
					<p className="text-base opacity-95">
						You have {dashboard?.todayAppointmentCount ?? 0} bookings scheduled for
						today. Let's make it a great day!
					</p>
				</div>
				<div className="flex gap-5">
					<div className="text-center px-5 py-3 bg-white/15 rounded-xl">
						<div className="text-3xl font-bold">
							{dashboard?.todayAppointmentCount ?? 0}
						</div>
						<div className="text-sm opacity-90">Today's Bookings</div>
					</div>
				</div>
			</div>

			{/* Error Alert */}
			{error && (
				<Alert
					type="warning"
					icon={<ExclamationTriangleIcon className="w-8 h-8" />}
					title="Unable to Load Data"
					message={error}
					actions={[{ label: 'Retry', onClick: handleRetry, primary: true }]}
					onDismiss={() => {}}
				/>
			)}

			{/* Password Change Success Alert */}
			{passwordJustChanged && showAlert('password-changed') && (
				<Alert
					type="success"
					icon={<CheckCircleIconSolid className="w-8 h-8" />}
					title="Password Changed Successfully!"
					message="Your password has been updated. You can now access all features of your staff account."
					onDismiss={() => dismissAlert('password-changed')}
				/>
			)}

			{/* Personal Info Request */}
			{showAlert('info-request') && infoRequestState === 'PENDING' && (
				<Alert
					type="warning"
					icon={<ShieldExclamationIcon className="w-8 h-8" />}
					title="We need some information from you"
					message={
						infoRequestExpiresOn
							? `Your administrator has requested your employment and payroll details. This request expires on ${infoRequestExpiresOn}.`
							: 'Your administrator has requested your employment and payroll details.'
					}
					actions={[
						{ label: 'Complete Now', onClick: openInfoRequestModal, primary: true },
					]}
					onDismiss={() => dismissAlert('info-request')}
				/>
			)}

			{showAlert('info-request-expired') && infoRequestState === 'EXPIRED' && (
				<Alert
					type="warning"
					icon={<ShieldExclamationIcon className="w-8 h-8" />}
					title="Information request expired"
					message="The request for your employment and payroll details has expired. Ask your administrator to send a new one."
					onDismiss={() => dismissAlert('info-request-expired')}
				/>
			)}

			{/* Setup Alerts */}
			{showAlert('policy-agreement') && profile && !profile.policyAgreementAccepted && (
				<Alert
					type="warning"
					icon={<DocumentTextIcon className="w-8 h-8" />}
					title="Review Policy Agreement"
					message="Please review and accept the staff policy agreement to ensure compliance with salon standards."
					actions={[
						{
							label: 'Review Now',
							onClick: () => navigate('/staff/agreements?open=policy'),
							primary: true,
						},
						{ label: 'Later', onClick: () => {}, dismissOnClick: true },
					]}
					onDismiss={() => dismissAlert('policy-agreement')}
				/>
			)}

			{showAlert('1099-agreement') && profile && !profile.agreement1099Accepted && (
				<Alert
					type="warning"
					icon={<ClipboardDocumentListIcon className="w-8 h-8" />}
					title="Accept 1099 Agreement"
					message="Please review and accept the 1099 independent contractor agreement to complete your setup."
					actions={[
						{
							label: 'Review Now',
							onClick: () => navigate('/staff/agreements?open=1099'),
							primary: true,
						},
						{ label: 'Later', onClick: () => {}, dismissOnClick: true },
					]}
					onDismiss={() => dismissAlert('1099-agreement')}
				/>
			)}

			{/* TODO: Re-enable once skills & tasks editing is enabled on Staff Profile
			{showAlert('skills-update') && profile && !profile.skillsUpdated && (
				<Alert
					type="info"
					icon={<WrenchScrewdriverIcon className="w-8 h-8" />}
					title="Update Your Skills"
					message="Select the services you can perform so you can be assigned to matching appointments."
					actions={[
						{
							label: 'Update Now',
							onClick: () => navigate('/staff/profile'),
							primary: true,
						},
						{ label: 'Later', onClick: () => {}, dismissOnClick: true },
					]}
					onDismiss={() => dismissAlert('skills-update')}
				/>
			)}

			{showAlert('tasks-update') && profile && !profile.tasksUpdated && (
				<Alert
					type="info"
					icon={<ClipboardDocumentListIcon className="w-8 h-8" />}
					title="Update Your Tasks"
					message="Select the task capabilities you can perform (e.g., Liner, Picker, Dropper, Feeder)."
					actions={[
						{
							label: 'Update Now',
							onClick: () => navigate('/staff/profile'),
							primary: true,
						},
						{ label: 'Later', onClick: () => {}, dismissOnClick: true },
					]}
					onDismiss={() => dismissAlert('tasks-update')}
				/>
			)}
			*/}

			{/* KPI Stats Grid */}
			<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
				{dashboardStats.map(stat => (
					<div
						key={stat.id}
						className={`bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border-2 border-transparent hover:border-[#667eea]${stat.link ? ' cursor-pointer' : ''}`}
						onClick={() => stat.link && navigate(stat.link)}
					>
						<div className="flex justify-between items-start md:mb-4">
							<div>
								<div className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-1">
									{stat.value}
								</div>
								<div className="text-sm text-gray-500 dark:text-gray-400 font-medium">
									{stat.label}
								</div>
							</div>
							<div
								className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${stat.colorClass}`}
							>
								<stat.icon className="w-6 h-6 text-gray-700" />
							</div>
						</div>
					</div>
				))}
			</div>

			{/* Today's Bookings - Full Width */}
			<div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm overflow-hidden">
				<div className="flex justify-between items-center p-5 border-b border-gray-200 dark:border-gray-700">
					<h2 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
						<CalendarDaysIcon className="w-5 h-5 text-[#667eea]" />
						Today's Bookings
					</h2>
					<Link
						to="/staff/bookings"
						className="text-[#667eea] hover:text-[#764ba2] font-medium text-sm transition-colors"
					>
						View All →
					</Link>
				</div>
				<div className="p-5">
					{bookings.length === 0 ? (
						<div className="py-8 text-center">
							<CalendarDaysIcon className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
							<p className="text-sm text-gray-500 dark:text-gray-400">
								No bookings for today
							</p>
						</div>
					) : (
						<div className="flex flex-col gap-3">
							{bookings.map((booking, index) => (
								<div
									key={booking.appointmentId}
									className="flex items-center gap-3.5 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border-l-[3px] border-l-transparent hover:bg-blue-50 dark:hover:bg-gray-700 hover:border-l-[#667eea] cursor-pointer transition-all hover:translate-x-1"
								>
									{/* Time */}
									<div className="text-center min-w-[60px]">
										<div className="font-bold text-gray-900 dark:text-white text-base">
											{booking.time || '-'}
										</div>
									</div>

									{/* Colored Divider */}
									<div
										className={`w-[3px] h-10 rounded-full ${DIVIDER_COLORS[index % DIVIDER_COLORS.length]}`}
									/>

									{/* Details */}
									<div className="flex-1 min-w-0">
										<div className="font-semibold text-gray-900 dark:text-white text-sm">
											{booking.customerName || 'Unknown'}
										</div>
										<div className="text-gray-500 dark:text-gray-400 text-xs mt-0.5">
											{[booking.serviceName, booking.size, booking.hairLength]
												.filter(Boolean)
												.join(' \u2022 ') || '-'}
										</div>
									</div>
								</div>
							))}
						</div>
					)}
				</div>
			</div>
		</AppLayout>
	);
};

export default StaffDashboard;
